import { CheckBoxList } from '@components/ui/checkbox-list';
import { Organization } from '@givz/core-client';
import { useSearchCharities } from '@queries/charities';
import HighlightOffSharpIcon from '@mui/icons-material/HighlightOffSharp';
import SearchSharpIcon from '@mui/icons-material/SearchSharp';
import {
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FunctionComponent, useEffect, useState } from 'react';

export type FeaturedCharitiesDialogProps = {
  onDone: (selectedItems: Map<string, Organization>) => void;
  selectedItems: Map<string, Organization>;
};

export const FeaturedCharitiesDialog: FunctionComponent<
  FeaturedCharitiesDialogProps
> = (props) => {
  const { onDone, selectedItems } = props;
  const [query, updateQuery] = useSearchCharities();
  const [selectedCharities, setSelectedCharities] = useState<
    Map<string, Organization>
  >(new Map());

  useEffect(() => {
    setSelectedCharities(new Map(selectedItems.entries()));

    return () => {
      setSelectedCharities(new Map());
    };
  }, [selectedItems]);

  const [search, setSearch] = useState('');
  const onClanSearch = () => setSearch('');
  const onChangeSearch = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const searchValue = event.target.value;
    setSearch(searchValue);
    if (searchValue.length > 2) {
      updateQuery({ q: searchValue });
    }
  };

  const onToggleItem = (params: { id: string; data?: Organization }) => {
    const { data, id } = params;
    if (selectedCharities.has(id)) {
      selectedCharities.delete(id);
    } else {
      selectedCharities.set(id, data!);
    }

    setSelectedCharities(new Map(selectedCharities.entries()));
  };

  const onDoneAction = () => {
    onDone(selectedCharities);
  };

  const isEmpty = query.data?.data.organizations.length === 0;

  return (
    <>
      <DialogTitle>Charity Search</DialogTitle>
      <DialogContent dividers>
        <Stack direction="column" spacing={1}>
          <TextField
            id="search-charity"
            label="Search for a charity"
            InputProps={{
              endAdornment: search ? (
                <InputAdornment
                  position="end"
                  onClick={onClanSearch}
                  sx={{ cursor: 'pointer' }}
                >
                  <HighlightOffSharpIcon />
                </InputAdornment>
              ) : null,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchSharpIcon />
                </InputAdornment>
              ),
            }}
            onChange={onChangeSearch}
            value={search}
            variant="outlined"
          />
          <Stack direction="row" flexWrap="wrap">
            {Array.from(selectedCharities.entries()).map(([id, data]) => (
              <Chip
                key={id}
                label={data.name}
                sx={{ marginBottom: 1, marginRight: 1 }}
                onDelete={() => onToggleItem({ data, id })}
              />
            ))}
          </Stack>
          {(!search || isEmpty) && (
            <DialogContentText py={10} textAlign="center">
              {isEmpty && (
                <Typography variant="body1" display="block" fontWeight={700}>
                  Sorry, no results found
                </Typography>
              )}
              <Typography variant="body2" display="block">
                Try searching "Red Cross" or by EIN "53-0196605"
              </Typography>
            </DialogContentText>
          )}
          {search && (
            <CheckBoxList
              checked={selectedCharities}
              onClickItem={onToggleItem}
              loading={query.isLoading}
              skeletonItems={3}
              items={
                query.data?.data.organizations.map((organization) => {
                  const { ein, gid, logoUrl, name } = organization;
                  return {
                    avatarSrc: logoUrl,
                    data: organization,
                    id: gid,
                    primary: name,
                    secondary: ein,
                  };
                }) ?? []
              }
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Typography variant="body2" display="block">
          Don't see what you're looking for?{' '}
          <Link href="mailto:support@givz.com" underline="hover">
            Contact us
          </Link>
        </Typography>
        <Button onClick={onDoneAction} variant="outlined">
          Done
        </Button>
      </DialogActions>
    </>
  );
};
